const VIDEO_URL_PATTERN =
  /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?[^<>\s"']+|youtu\.be\/[^<>\s"']+|youtube\.com\/embed\/[^<>\s"']+|vimeo\.com\/[^<>\s"']+|player\.vimeo\.com\/video\/[^<>\s"']+|[^<>\s"']+\.(?:mp4|webm|ogg)(?:\?[^<>\s"']*)?)/i;

const DIRECT_VIDEO_REGEX = /\.(mp4|webm|ogg)(\?.*)?$/i;
const MEDIA_TAG_REGEX = /<(iframe|video)\b[\s\S]*?<\/\1>/gi;
const RESPONSIVE_MEDIA_STYLE =
  'display:block;width:100%;max-width:100%;aspect-ratio:16/9;height:auto;border:0;margin:2rem 0;';
const RESPONSIVE_VIDEO_STYLE = `${RESPONSIVE_MEDIA_STYLE}object-fit:contain;background:#000;`;

const getYouTubeId = (input: string) => {
  try {
    const url = new URL(input);
    const host = url.hostname.replace(/^www\./, '');

    if (host === 'youtu.be') {
      return url.pathname.split('/').filter(Boolean)[0];
    }

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (url.pathname.startsWith('/embed/')) {
        return url.pathname.split('/').filter(Boolean)[1];
      }
      return url.searchParams.get('v') || undefined;
    }
  } catch {
    return undefined;
  }

  return undefined;
};

const getVimeoId = (input: string) => {
  try {
    const url = new URL(input);
    const host = url.hostname.replace(/^www\./, '');

    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      return url.pathname.split('/').filter(Boolean).pop();
    }
  } catch {
    return undefined;
  }

  return undefined;
};

const createEmbedNode = (documentRef: Document, input: string) => {
  const value = input.trim();
  const youtubeId = getYouTubeId(value);
  const vimeoId = getVimeoId(value);
  const wrapper = documentRef.createElement('div');
  wrapper.className = 'rich-text-media';

  if (youtubeId || vimeoId) {
    const iframe = documentRef.createElement('iframe');
    iframe.src = youtubeId
      ? `https://www.youtube.com/embed/${youtubeId}`
      : `https://player.vimeo.com/video/${vimeoId}`;
    iframe.title = 'Embedded video';
    iframe.setAttribute('style', RESPONSIVE_MEDIA_STYLE);
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
    );
    wrapper.appendChild(iframe);
    return wrapper;
  }

  if (DIRECT_VIDEO_REGEX.test(value)) {
    const video = documentRef.createElement('video');
    video.src = value;
    video.controls = true;
    video.setAttribute('style', RESPONSIVE_VIDEO_STYLE);
    wrapper.appendChild(video);
    return wrapper;
  }

  return null;
};

const appendStyle = (openingTag: string, style: string) => {
  const styleMatch = openingTag.match(/\sstyle=(["'])(.*?)\1/i);

  if (styleMatch) {
    return openingTag.replace(
      styleMatch[0],
      ` style=${styleMatch[1]}${styleMatch[2].trim().replace(/;?$/, ';')}${style}${styleMatch[1]}`
    );
  }

  return openingTag.replace(/>$/, ` style="${style}">`);
};

const makeMediaTagResponsive = (mediaHtml: string) => {
  if (/^<iframe\b/i.test(mediaHtml)) {
    return mediaHtml.replace(/^<iframe\b[^>]*>/i, (openingTag) =>
      appendStyle(openingTag, RESPONSIVE_MEDIA_STYLE)
    );
  }

  if (/^<video\b/i.test(mediaHtml)) {
    return mediaHtml.replace(/^<video\b[^>]*>/i, (openingTag) =>
      appendStyle(openingTag, RESPONSIVE_VIDEO_STYLE)
    );
  }

  return mediaHtml;
};

const replaceBareVideoLinks = (root: ParentNode) => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];

  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text);
  }

  textNodes.forEach((node) => {
    const value = node.textContent?.trim() || '';
    const match = value.match(VIDEO_URL_PATTERN);
    if (!match || value !== match[0]) {
      return;
    }

    const embedNode = createEmbedNode(node.ownerDocument, match[0]);
    if (!embedNode) {
      return;
    }

    const parent = node.parentElement;
    if (parent && parent.textContent?.trim() === match[0]) {
      parent.replaceWith(embedNode);
      return;
    }

    node.replaceWith(embedNode);
  });
};

export const enhanceRichTextMedia = (html: string) => {
  if (!html) {
    return html;
  }

  const htmlWithWrappedMedia = html.replace(MEDIA_TAG_REGEX, (mediaHtml) => {
    const responsiveMediaHtml = makeMediaTagResponsive(mediaHtml);

    if (mediaHtml.includes('class="rich-text-media"')) {
      return responsiveMediaHtml;
    }

    return `<div class="rich-text-media">${responsiveMediaHtml}</div>`;
  });

  if (typeof window === 'undefined') {
    return htmlWithWrappedMedia;
  }

  const template = document.createElement('template');
  template.innerHTML = htmlWithWrappedMedia;
  replaceBareVideoLinks(template.content);
  return template.innerHTML;
};
